import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationProps {
  isVisible?: boolean;
  setIsVisible?: (visible: boolean) => void;
}

export default function Navigation({ isVisible = false, setIsVisible }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);
  const [charterDropdownOpen, setCharterDropdownOpen] = useState(false);
  const [internalMenuVisible, setInternalMenuVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Use internal state if setIsVisible is not provided
  const menuVisible = isVisible || internalMenuVisible;
  const setMenuVisible = setIsVisible || setInternalMenuVisible;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCharterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      scrollToSection('hero');
    } else {
      navigate('/');
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setCharterDropdownOpen(false);
    if (setIsVisible) {
      setIsVisible(false);
    } else {
      setInternalMenuVisible(false);
    }
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleAboutClick = () => {
    navigate('/about');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
      <div className="px-8 lg:px-16 py-5">
        <div className="flex items-center justify-between">
          <button 
            onClick={handleLogoClick}
            className={`text-xl font-serif tracking-wide hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
          >
            SYG YACHTS
          </button>
          
          <div className="hidden lg:flex items-center gap-8">
            <button 
              onClick={handleAboutClick} 
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              ABOUT
            </button>
            
            {/* Charter Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setCharterDropdownOpen(!charterDropdownOpen)}
                className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer flex items-center gap-1 ${scrolled ? 'text-black' : 'text-white'}`}
              >
                CHARTER
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${charterDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {charterDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white shadow-lg rounded-md py-2 min-w-48 border border-gray-100">
                  <button 
                    onClick={() => handleNavigation('/yacht-charter')}
                    className="w-full text-left px-4 py-2.5 text-xs tracking-wider text-black hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    YACHTS FOR CHARTER
                  </button>
                  <button 
                    onClick={() => handleNavigation('/destinations')}
                    className="w-full text-left px-4 py-2.5 text-xs tracking-wider text-black hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    DESTINATIONS
                  </button>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => handleNavigation('/yacht-sales')}
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              SALES
            </button>
            
            <button 
              onClick={() => handleNavigation('/new-build-management')}
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              NEW BUILD
            </button>
            
            <button 
              onClick={() => handleNavigation('/refit-management')}
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              REFIT
            </button>
            
            <button 
              onClick={() => handleNavigation('/crew-placement')}
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              CREW
            </button>
            
            <button 
              onClick={() => handleNavigation('/yacht-financing')}
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              FINANCING
            </button>
            
            <button 
              onClick={() => scrollToSection('contact')} 
              className={`text-xs tracking-wider hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${scrolled ? 'text-black' : 'text-white'}`}
            >
              CONTACT
            </button>
          </div>

          <button 
            onClick={() => setMenuVisible(!menuVisible)}
            className="lg:hidden w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
          >
            <span className={`w-6 h-0.5 transition-all ${scrolled ? 'bg-black' : 'bg-white'} ${menuVisible ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`w-6 h-0.5 transition-all ${scrolled ? 'bg-black' : 'bg-white'} ${menuVisible ? 'opacity-0' : ''}`}></span>
            <span className={`w-6 h-0.5 transition-all ${scrolled ? 'bg-black' : 'bg-white'} ${menuVisible ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuVisible && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-8 py-6 flex flex-col gap-4">
            <button 
              onClick={() => { scrollToSection('about'); setMenuVisible(false); }} 
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              ABOUT
            </button>
            
            {/* Mobile Charter Section */}
            <div className="border-l-2 border-gray-200 pl-4">
              <p className="text-sm tracking-wider text-gray-500 mb-3">CHARTER</p>
              <button 
                onClick={() => handleNavigation('/yacht-charter')}
                className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer block mb-3"
              >
                YACHTS FOR CHARTER
              </button>
              <button 
                onClick={() => handleNavigation('/destinations')}
                className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer block"
              >
                DESTINATIONS
              </button>
            </div>
            
            <button 
              onClick={() => handleNavigation('/yacht-sales')}
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              SALES
            </button>
            
            <button 
              onClick={() => handleNavigation('/new-build-management')}
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              NEW BUILD
            </button>
            
            <button 
              onClick={() => handleNavigation('/refit-management')}
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              REFIT
            </button>
            
            <button 
              onClick={() => handleNavigation('/crew-placement')}
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              CREW
            </button>
            
            <button 
              onClick={() => handleNavigation('/yacht-financing')}
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              FINANCING
            </button>
            
            <button 
              onClick={() => { scrollToSection('contact'); setMenuVisible(false); }} 
              className="text-sm tracking-wider text-black hover:opacity-70 transition-opacity text-left whitespace-nowrap cursor-pointer"
            >
              CONTACT
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
